import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { AxiosResponse, isAxiosError } from 'axios';

import { get, post } from '@api/httpClient';
import { APIError } from '@api/types';
import {
  type FlowgateDistinctResponse,
  type FlowgatePipelineList,
  type FlowgatePipelineDetail,
  type FlowgateQueryDomainRequest,
  type FlowgateQueryRequest,
  type FlowgateQueryResponse,
  type FlowgateResultErrorBody,
  type FlowgateResultList,
  type FlowgateResultSchemaResponse,
  type FlowgateScalar,
  type RunJobRequest,
  type RunJobResponse,
} from '@api/flowgate/types';

const QUERY_KEYS = {
  PIPELINES: ['flowgate', 'pipelines'],
  PIPELINE: (pipelineId: string) => [
    'flowgate',
    'pipelines',
    { id: pipelineId },
  ],
  FLOWGATE_RESULTS: (experimentId: string) => [
    'flowgate',
    'results',
    { experiment: experimentId },
  ],
  FLOWGATE_RESULT_SCHEMA: (resultId: string) => [
    'flowgate',
    'results',
    { id: resultId },
    'schema',
  ],
  FLOWGATE_RESULT_DISTINCT: (resultId: string, column: string) => [
    'flowgate',
    'results',
    { id: resultId },
    'distinct',
    { column },
  ],
};

// ──────────────────────────────────────────────────────────────────────────────
// Result data error helpers
//   409 — result not ready yet (still converting / failed)
//   503 — pipeline service unreachable
//   400/404 — bad request / unknown result (surface server `detail`)
// ──────────────────────────────────────────────────────────────────────────────

export type FlowgateResultErrorKind =
  | 'not_ready'
  | 'unavailable'
  | 'not_found'
  | 'forbidden'
  | 'bad_request'
  | 'no_row_id'
  | 'unknown';

export type FlowgateResultErrorInfo = {
  kind: FlowgateResultErrorKind;
  message: string;
  status?: number;
};

export const classifyFlowgateResultError = (
  error: unknown,
): FlowgateResultErrorInfo => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const detail = (error.response?.data as FlowgateResultErrorBody | undefined)
      ?.detail;

    if (status === 409) {
      // Two typed 409s: result-not-ready (parquet still converting) and
      // no_row_id (mutating call on a pre-phase-2 result). The BE sets
      // `code: 'no_row_id'` on the latter.
      const body = error.response?.data as FlowgateResultErrorBody | undefined;
      if (body?.code === 'no_row_id') {
        return {
          kind: 'no_row_id',
          message:
            detail ??
            'This result was produced before row selection was supported. Re-run the pipeline to enable it.',
          status,
        };
      }
      return {
        kind: 'not_ready',
        message: detail ?? 'Result not ready yet, try again shortly.',
        status,
      };
    }
    if (status === 503) {
      return {
        kind: 'unavailable',
        message: detail ?? 'Pipeline service unavailable, please retry.',
        status,
      };
    }
    if (status === 404) {
      return {
        kind: 'not_found',
        message: detail ?? 'Result not found.',
        status,
      };
    }
    if (status === 403) {
      return {
        kind: 'forbidden',
        message: detail ?? 'You do not have access to this result.',
        status,
      };
    }
    if (status === 400) {
      return {
        kind: 'bad_request',
        message: detail ?? 'Invalid query.',
        status,
      };
    }
  }
  return { kind: 'unknown', message: 'Failed to load result.' };
};

export const useListPipelines = () =>
  useSuspenseQuery<FlowgatePipelineList>({
    queryKey: QUERY_KEYS.PIPELINES,
    queryFn: async () => {
      const resp = await get<FlowgatePipelineList>('flowgate/pipelines/');
      return resp.data;
    },
  });

export const usePipelineDetail = (pipelineId?: string) =>
  useSuspenseQuery<FlowgatePipelineDetail | null>({
    queryKey: QUERY_KEYS.PIPELINE(pipelineId || ''),
    queryFn: async () => {
      if (!pipelineId) return null;

      const resp = await get<FlowgatePipelineDetail>(
        `flowgate/pipelines/${pipelineId}/`,
      );
      return resp.data;
    },
  });

export const useRunJob = () =>
  useMutation<AxiosResponse<RunJobResponse>, APIError, RunJobRequest>({
    mutationFn: ({ experiment_id, params }) =>
      post<RunJobResponse>('flowgate/run_job/', { experiment_id, params }),
  });

export const useFlowgateResults = (experimentId?: string) =>
  useSuspenseQuery<FlowgateResultList>({
    queryKey: QUERY_KEYS.FLOWGATE_RESULTS(experimentId || ''),
    queryFn: async () => {
      if (!experimentId) return [];

      const resp = await get<FlowgateResultList>(
        `flowgate/results/?experiment=${experimentId}`,
      );
      return resp.data;
    },
  });

// GET /flowgate/results/<id>/schema/ — column list + types + row count. Used
// to build dynamic DataGrid columns and coerce filter values per column type.
export const useFlowgateResultSchema = (resultId?: string) =>
  useQuery<FlowgateResultSchemaResponse | null>({
    queryKey: QUERY_KEYS.FLOWGATE_RESULT_SCHEMA(resultId || ''),
    enabled: !!resultId,
    queryFn: async () => {
      if (!resultId) return null;
      const resp = await get<FlowgateResultSchemaResponse>(
        `flowgate/results/${resultId}/schema/`,
      );
      return resp.data;
    },
    retry: (failureCount, error) => {
      // Don't retry on terminal client errors (409 not-ready, 404, 403, 400).
      const { kind } = classifyFlowgateResultError(error);
      if (
        kind === 'not_ready' ||
        kind === 'not_found' ||
        kind === 'forbidden' ||
        kind === 'bad_request'
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

// POST /flowgate/results/<id>/query/ — paged/sorted/filtered data.
// Modelled as a mutation because each (paginationModel, sortModel, filterModel)
// change is a new server request the page initiates; react-query treats it like
// any other useMutation.
export type FlowgateQueryParams = {
  resultId: string;
  body: FlowgateQueryRequest;
};

export const useFlowgateResultQuery = () =>
  useMutation<
    AxiosResponse<FlowgateQueryResponse>,
    APIError,
    FlowgateQueryParams
  >({
    mutationFn: ({ resultId, body }) =>
      post<FlowgateQueryResponse>(`flowgate/results/${resultId}/query/`, body),
  });

// POST /flowgate/results/<id>/query-domain/ — legacy-parity domain filters
// (HPO / gene panel / gene include-exclude resolved server-side, plus
// vcf/annotation column predicates). Same response shape as /query/.
export type FlowgateQueryDomainParams = {
  resultId: string;
  body: FlowgateQueryDomainRequest;
};

export const useFlowgateResultQueryDomain = () =>
  useMutation<
    AxiosResponse<FlowgateQueryResponse>,
    APIError,
    FlowgateQueryDomainParams
  >({
    mutationFn: ({ resultId, body }) =>
      post<FlowgateQueryResponse>(
        `flowgate/results/${resultId}/query-domain/`,
        body,
      ),
  });

// GET /flowgate/results/<id>/distinct/?column=...&limit=... — values for an
// enum-like filter dropdown.
export const useFlowgateResultDistinct = (
  resultId?: string,
  column?: string,
  limit = 1000,
) =>
  useQuery<Array<FlowgateScalar | null>>({
    queryKey: QUERY_KEYS.FLOWGATE_RESULT_DISTINCT(resultId || '', column || ''),
    enabled: !!resultId && !!column,
    queryFn: async () => {
      if (!resultId || !column) return [];
      const resp = await get<FlowgateDistinctResponse>(
        `flowgate/results/${resultId}/distinct/?column=${encodeURIComponent(
          column,
        )}&limit=${limit}`,
      );
      return resp.data.values;
    },
  });

// Build the absolute URL for /flowgate/results/<id>/download/?format=...
// using the same baseURL the axios httpClient uses. CSV/TSV are streamed
// directly (open via window.open); Parquet returns JSON {url, filename}.
export const getFlowgateResultDownloadUrl = (
  resultId: string,
  format: 'csv' | 'tsv' | 'parquet',
): string => {
  const base = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
  return `${base}/flowgate/results/${resultId}/download/?format=${format}`;
};

// ──────────────────────────────────────────────────────────────────────────────
// Report generation (reuses the legacy Synapsys Reporting integration)
// ──────────────────────────────────────────────────────────────────────────────

export type FlowgateReportType = 'TEST' | 'CANCER' | 'ANCESTRY' | 'HORIZON';

export type SubmitFlowgateReportParams = {
  resultId: string;
  reportType: FlowgateReportType;
};

export type SubmitFlowgateReportResponse = {
  id: string;
  message: string;
  report_type: string;
  report_id: string;
};

// POST /flowgate/results/<id>/report/submit/<report_type>/
// The BE derives the variant list itself from the user's validated selections,
// so no body is needed.
export const useSubmitFlowgateReport = () =>
  useMutation<
    AxiosResponse<SubmitFlowgateReportResponse>,
    APIError,
    SubmitFlowgateReportParams
  >({
    mutationFn: ({ resultId, reportType }) =>
      post<SubmitFlowgateReportResponse>(
        `flowgate/results/${resultId}/report/submit/${reportType}/`,
        {},
      ),
  });

// GET /flowgate/results/<id>/report/download_latest/ — streams the latest
// completed report PDF (404 until a report has finished generating).
export const downloadFlowgateReportLatest = (resultId: string) =>
  get<Blob>(`flowgate/results/${resultId}/report/download_latest/`, {
    responseType: 'blob',
  });

export type FlowgateReportStatus = 'PENDING' | 'SUCCESS' | 'FAILED';

export type FlowgateReport = {
  id: string;
  report_type: string;
  status: FlowgateReportStatus;
  report_id: string | null;
  download_url: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
};

// GET /flowgate/results/<id>/report/ — the result's reports, newest first.
// Polls every 5s while any report is still PENDING so the status updates live.
export const useFlowgateReports = (resultId?: string) =>
  useQuery<FlowgateReport[]>({
    queryKey: ['flowgate', 'reports', { result: resultId ?? '' }],
    enabled: !!resultId,
    queryFn: async () => {
      if (!resultId) return [];
      const resp = await get<FlowgateReport[]>(
        `flowgate/results/${resultId}/report/`,
      );
      return resp.data;
    },
    refetchInterval: (query) => {
      const { data } = query.state;
      return data && data.some((r) => r.status === 'PENDING') ? 5000 : false;
    },
  });
